/**
 * Unity Variables File Renderer
 * Generates comprehensive variable classes with type-safe references and value types
 * Supports: string, number, percentage, range, boolean variables with linked references
 */

/**
 * Render variables file for Unity C# exports
 * Generates comprehensive variable classes with type-safe references and value types
 * 
 * @param variables - Array of variable definitions
 * @param namespace - C# namespace (default: 'Grimoire.Data')
 * @param className - Class name for the static variables class (default: 'Variables')
 */
export function renderVariablesFile(
  variables: any[],
  namespace: string = 'Grimoire.Data',
  className: string = 'Variables'
): string {
  console.log(`📊 Rendering variables file for namespace: ${namespace}`);
  console.log(`🔢 Processing ${variables.length} variables`);
  
  let output = '// Auto-generated Variables\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += 'using System;\n\n';
  output += `namespace ${namespace}\n{\n`;
  
  // Generate base VariableElement class
  output += '\t/// <summary>Base class for all variable elements</summary>\n';
  output += '\tpublic abstract class VariableElement\n\t{\n';
  output += '\t\tpublic string Id { get; set; }\n';
  output += '\t\tpublic string Name { get; set; }\n';
  output += '\t\tpublic string Description { get; set; }\n';
  output += '\t}\n\n';
  
  // Generate StringVariableElement
  output += '\t/// <summary>String variable element</summary>\n';
  output += '\tpublic class StringVariableElement : VariableElement\n\t{\n';
  output += '\t\tpublic string Value => Name;\n\n';
  output += '\t\t/// <summary>Implicit conversion to string for seamless usage</summary>\n';
  output += '\t\tpublic static implicit operator string(StringVariableElement element)\n\t\t{\n';
  output += '\t\t\treturn element?.Value ?? "";\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate NumberVariableElement
  output += '\t/// <summary>Number variable element with optional linked value</summary>\n';
  output += '\tpublic class NumberVariableElement : VariableElement\n\t{\n';
  output += '\t\tpublic double? NumericValue { get; set; }\n';
  output += '\t\tpublic Func<NumberVariableElement> LinkedValueGetter { get; set; }\n\n';
  output += '\t\t/// <summary>Gets the effective numeric value, following linked references</summary>\n';
  output += '\t\tpublic double GetValue()\n\t\t{\n';
  output += '\t\t\tif (LinkedValueGetter != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar linkedElement = LinkedValueGetter();\n';
  output += '\t\t\t\treturn linkedElement?.GetValue() ?? NumericValue ?? 0.0;\n';
  output += '\t\t\t}\n';
  output += '\t\t\treturn NumericValue ?? 0.0;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Implicit conversion to double for seamless usage</summary>\n';
  output += '\t\tpublic static implicit operator double(NumberVariableElement element)\n\t\t{\n';
  output += '\t\t\treturn element?.GetValue() ?? 0.0;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Implicit conversion to float for Unity compatibility</summary>\n';
  output += '\t\tpublic static implicit operator float(NumberVariableElement element)\n\t\t{\n';
  output += '\t\t\treturn (float)(element?.GetValue() ?? 0.0);\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate PercentageVariableElement
  output += '\t/// <summary>Percentage variable element with optional linked value</summary>\n';
  output += '\tpublic class PercentageVariableElement : VariableElement\n\t{\n';
  output += '\t\tpublic double? PercentageValue { get; set; }\n';
  output += '\t\tpublic Func<PercentageVariableElement> LinkedValueGetter { get; set; }\n\n';
  output += '\t\t/// <summary>Gets the effective percentage value (0-100), following linked references</summary>\n';
  output += '\t\tpublic double GetValue()\n\t\t{\n';
  output += '\t\t\tif (LinkedValueGetter != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar linkedElement = LinkedValueGetter();\n';
  output += '\t\t\t\treturn linkedElement?.GetValue() ?? PercentageValue ?? 0.0;\n';
  output += '\t\t\t}\n';
  output += '\t\t\treturn PercentageValue ?? 0.0;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Gets the value as a normalized decimal (0.0-1.0)</summary>\n';
  output += '\t\tpublic double GetNormalizedValue() => GetValue() / 100.0;\n\n';
  output += '\t\t/// <summary>Implicit conversion to double (returns percentage 0-100)</summary>\n';
  output += '\t\tpublic static implicit operator double(PercentageVariableElement element)\n\t\t{\n';
  output += '\t\t\treturn element?.GetValue() ?? 0.0;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Implicit conversion to float for Unity compatibility</summary>\n';
  output += '\t\tpublic static implicit operator float(PercentageVariableElement element)\n\t\t{\n';
  output += '\t\t\treturn (float)(element?.GetNormalizedValue() ?? 0.0);\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate BooleanVariableElement
  output += '\t/// <summary>Boolean variable element with optional linked value</summary>\n';
  output += '\tpublic class BooleanVariableElement : VariableElement\n\t{\n';
  output += '\t\tpublic bool? BooleanValue { get; set; }\n';
  output += '\t\tpublic Func<BooleanVariableElement> LinkedValueGetter { get; set; }\n\n';
  output += '\t\t/// <summary>Gets the effective boolean value, following linked references</summary>\n';
  output += '\t\tpublic bool GetValue()\n\t\t{\n';
  output += '\t\t\tif (LinkedValueGetter != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar linkedElement = LinkedValueGetter();\n';
  output += '\t\t\t\treturn linkedElement?.GetValue() ?? BooleanValue ?? false;\n';
  output += '\t\t\t}\n';
  output += '\t\t\treturn BooleanValue ?? false;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Implicit conversion to bool for seamless usage</summary>\n';
  output += '\t\tpublic static implicit operator bool(BooleanVariableElement element)\n\t\t{\n';
  output += '\t\t\treturn element?.GetValue() ?? false;\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate RangeVariableElement
  output += '\t/// <summary>Range variable element with min/max/increment and optional linked value</summary>\n';
  output += '\tpublic class RangeVariableElement : VariableElement\n\t{\n';
  output += '\t\tpublic double? RangeMin { get; set; }\n';
  output += '\t\tpublic double? RangeMax { get; set; }\n';
  output += '\t\tpublic double? Increment { get; set; }\n';
  output += '\t\tpublic Func<RangeVariableElement> LinkedValueGetter { get; set; }\n\n';
  output += '\t\t/// <summary>Gets the minimum value, following linked references</summary>\n';
  output += '\t\tpublic double GetMin()\n\t\t{\n';
  output += '\t\t\tif (LinkedValueGetter != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar linkedElement = LinkedValueGetter();\n';
  output += '\t\t\t\treturn linkedElement?.GetMin() ?? RangeMin ?? 0.0;\n';
  output += '\t\t\t}\n';
  output += '\t\t\treturn RangeMin ?? 0.0;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Gets the maximum value, following linked references</summary>\n';
  output += '\t\tpublic double GetMax()\n\t\t{\n';
  output += '\t\t\tif (LinkedValueGetter != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar linkedElement = LinkedValueGetter();\n';
  output += '\t\t\t\treturn linkedElement?.GetMax() ?? RangeMax ?? 0.0;\n';
  output += '\t\t\t}\n';
  output += '\t\t\treturn RangeMax ?? 0.0;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Gets the increment/step value, following linked references</summary>\n';
  output += '\t\tpublic double GetIncrement()\n\t\t{\n';
  output += '\t\t\tif (LinkedValueGetter != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar linkedElement = LinkedValueGetter();\n';
  output += '\t\t\t\treturn linkedElement?.GetIncrement() ?? Increment ?? 1.0;\n';
  output += '\t\t\t}\n';
  output += '\t\t\treturn Increment ?? 1.0;\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate TypeVariableElement (references to objects as types)
  output += '\t/// <summary>Type variable element that can reference game objects</summary>\n';
  output += '\tpublic class TypeVariableElement : VariableElement\n\t{\n';
  output += '\t\tpublic Func<VariableElement> LinkedValueGetter { get; set; }\n\n';
  output += '\t\t/// <summary>Gets the linked value element (type-safe reference)</summary>\n';
  output += '\t\tpublic VariableElement GetLinkedValue()\n\t\t{\n';
  output += '\t\t\treturn LinkedValueGetter?.Invoke();\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate main Variables static class
  output += `\t/// <summary>Static accessor for all game variables</summary>\n`;
  output += `\tpublic static class ${className}\n\t{\n`;
  
  // Create a lookup map for resolving linked references
  const variableMap = new Map<string, any>();
  variables.forEach(v => variableMap.set(v.id, v));
  
  // Helper function to sanitize C# identifiers
  const sanitizeName = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9_]/g, '_');
  };
  
  // Helper function to get C# type for variable
  const getCSharpType = (elementType: string): string => {
    switch (elementType) {
      case 'string': return 'StringVariableElement';
      case 'number': return 'NumberVariableElement';
      case 'percentage': return 'PercentageVariableElement';
      case 'range': return 'RangeVariableElement';
      case 'type': return 'TypeVariableElement';
      case 'boolean': return 'BooleanVariableElement';
      default: return 'StringVariableElement';
    }
  };
  
  // Process each variable
  variables.forEach((variable, varIndex) => {
    if (!variable.data || !Array.isArray(variable.data) || variable.data.length === 0) {
      return;
    }
    
    const variableName = sanitizeName(variable.name || `Variable${varIndex}`);
    const elementType = variable.element_type || 'string';
    const csharpType = getCSharpType(elementType);
    
    console.log(`  📦 Processing variable: ${variableName} (type: ${elementType}, ${variable.data.length} elements)`);
    
    // Create nested class for this variable
    output += `\t\t/// <summary>${variable.description || variable.name || variableName}</summary>\n`;
    output += `\t\tpublic static class ${variableName}\n\t\t{\n`;
    
    // Process each element in the variable
    variable.data.forEach((element: any) => {
      const elementName = sanitizeName(element.name || element.id || 'Unknown');
      const elementId = element.id || '';
      const escapedId = elementId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const escapedName = (element.name || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const escapedDesc = (element.description || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      
      output += `\t\t\t/// <summary>${element.description || element.name || elementName}</summary>\n`;
      output += `\t\t\tpublic static readonly ${csharpType} ${elementName} = new()\n\t\t\t{\n`;
      output += `\t\t\t\tId = "${escapedId}",\n`;
      output += `\t\t\t\tName = "${escapedName}",\n`;
      output += `\t\t\t\tDescription = "${escapedDesc}"`;
      
      // Add type-specific properties
      if (elementType === 'number') {
        if (element.numericValue !== undefined && element.numericValue !== null) {
          output += `,\n\t\t\t\tNumericValue = ${element.numericValue}`;
        }
      } else if (elementType === 'percentage') {
        if (element.numericValue !== undefined && element.numericValue !== null) {
          output += `,\n\t\t\t\tPercentageValue = ${element.numericValue}`;
        }
      } else if (elementType === 'boolean') {
        if (element.numericValue !== undefined && element.numericValue !== null) {
          const boolValue = element.numericValue === 1 ? 'true' : 'false';
          output += `,\n\t\t\t\tBooleanValue = ${boolValue}`;
        }
      } else if (elementType === 'range') {
        if (element.rangeMin !== undefined && element.rangeMin !== null) {
          output += `,\n\t\t\t\tRangeMin = ${element.rangeMin}`;
        }
        if (element.rangeMax !== undefined && element.rangeMax !== null) {
          output += `,\n\t\t\t\tRangeMax = ${element.rangeMax}`;
        }
        if (element.increment !== undefined && element.increment !== null) {
          output += `,\n\t\t\t\tIncrement = ${element.increment}`;
        }
      }
      
      // Add linked value getter for type-safe references
      if (element.linkedVariableId && element.linkedElementId) {
        const linkedVar = variableMap.get(element.linkedVariableId);
        if (linkedVar) {
          const linkedVarName = sanitizeName(linkedVar.name || 'Unknown');
          const linkedElement = linkedVar.data?.find((e: any) => e.id === element.linkedElementId);
          if (linkedElement) {
            const linkedElemName = sanitizeName(linkedElement.name || 'Unknown');
            output += `,\n\t\t\t\tLinkedValueGetter = () => ${linkedVarName}.${linkedElemName}`;
          }
        }
      }
      
      output += '\n\t\t\t};\n\n';
    });
    
    output += '\t\t}\n\n';
  });
  
  output += '\t}\n';
  output += '}\n';
  
  console.log('✅ Variables file rendered successfully');
  
  return output;
}
